import { extensions } from "@repo/fika-blog/src/extensions"
import {
  generateHTML,
  generateJSON,
  type JSONContent,
} from "@repo/fika-blog/src/tiptap/core"
import {
  getLanguageLabel,
  isLocale,
  type Locale,
} from "@repo/fika-shared/src/locale"
import {
  parseTranslatableField,
  type TranslatableData,
} from "@repo/fika-shared/src/translate"
import {
  type Accessor,
  createContext,
  createEffect,
  createMemo,
  createSignal,
  type JSX,
  on,
  onCleanup,
  onMount,
  type ParentProps,
  Show,
  useContext,
} from "solid-js"
import { Button } from "@/designSystem/components/button"
import { Icon } from "@/designSystem/components/icon"
import { Mini } from "@/designSystem/components/mini/mini"
import { graphql } from "@/graphql"
import { useI18nContext } from "@/i18n/i18n-solid"
import {
  createMutationWithErrorHandling,
  ErrorFragment,
} from "@/lib/graphql-mutation-helpers"
import {
  generatedStatusClass,
  selectWrapperClass,
  wrapperClass,
} from "./translatable-field.css"

interface FieldRegistration {
  name: string
  format: "text" | "tiptap"
  getValue: () => string
  setTranslations: (
    translations: Partial<Record<Locale, string>>,
    options?: {
      persist?: boolean
    },
  ) => void | Promise<void>
}

interface TranslatableFieldGroupContext {
  registerField: (reg: FieldRegistration) => void
  unregisterField: (name: string) => void
  currentLocale: Accessor<Locale>
  setCurrentLocale: (locale: Locale) => void
  availableLocales: Locale[]
  defaultLocale: Locale
  isTranslating: Accessor<boolean>
  setIsTranslating: (value: boolean) => void
  getRegisteredFields: () => Map<string, FieldRegistration>
}

const TranslatableFieldGroupContext =
  createContext<TranslatableFieldGroupContext>()

export function useTranslatableFieldGroup() {
  return useContext(TranslatableFieldGroupContext)
}

interface TranslatableFieldGroupProps extends ParentProps {
  availableLocales: Locale[]
  defaultLocale: Locale
}

export function TranslatableFieldGroup(props: TranslatableFieldGroupProps) {
  const [currentLocale, setCurrentLocale] = createSignal<Locale>(
    props.defaultLocale,
  )
  const [isTranslating, setIsTranslating] = createSignal(false)
  const registeredFields = new Map<string, FieldRegistration>()

  function registerField(reg: FieldRegistration) {
    registeredFields.set(reg.name, reg)
  }

  function unregisterField(name: string) {
    registeredFields.delete(name)
  }

  function getRegisteredFields() {
    return registeredFields
  }

  const contextValue: TranslatableFieldGroupContext = {
    registerField,
    unregisterField,
    currentLocale,
    setCurrentLocale,
    get availableLocales() {
      return props.availableLocales
    },
    get defaultLocale() {
      return props.defaultLocale
    },
    isTranslating,
    setIsTranslating,
    getRegisteredFields,
  }

  return (
    <TranslatableFieldGroupContext.Provider value={contextValue}>
      {props.children}
    </TranslatableFieldGroupContext.Provider>
  )
}

interface TranslatableFieldContext {
  currentLocale: Accessor<Locale>
  setCurrentLocale: (locale: Locale) => void
  availableLocales: Locale[]
  defaultLocale: Locale
  isDirty: Accessor<boolean>
  syncContent: (text: string, locale: Locale) => void
  resetLocale: () => void
}

const TranslatableFieldContext = createContext<TranslatableFieldContext>()

export function useTranslatableField() {
  return useContext(TranslatableFieldContext)
}

interface TranslatableFieldProps {
  value: string
  onChange: (value: string) => void | Promise<void>
  availableLocales: Locale[]
  defaultLocale: Locale
  propagateGeneratedTranslations?: boolean
  name?: string
  format?: "text" | "tiptap"
  children: (
    value: Accessor<string>,
    onChange: (value: string) => void,
  ) => JSX.Element
}

function parseTranslatableValue(
  value: string,
  defaultLocale: Locale,
): TranslatableData {
  if (!value) return { primaryLocale: defaultLocale, values: {} }

  const parsed = parseTranslatableField(value)
  if (parsed) return parsed

  return { primaryLocale: defaultLocale, values: { [defaultLocale]: value } }
}

export function TranslatableField(props: TranslatableFieldProps) {
  const groupContext = useTranslatableFieldGroup()

  const [localCurrentLocale, setLocalCurrentLocale] = createSignal<Locale>(
    props.defaultLocale,
  )

  const currentLocale = groupContext
    ? groupContext.currentLocale
    : localCurrentLocale

  const setCurrentLocale = groupContext
    ? groupContext.setCurrentLocale
    : (locale: Locale) => setLocalCurrentLocale(locale)

  const [translations, setTranslations] = createSignal<TranslatableData>(
    parseTranslatableValue(props.value, props.defaultLocale),
  )
  const [baseline, setBaseline] = createSignal(
    JSON.stringify(parseTranslatableValue(props.value, props.defaultLocale)),
  )

  const scopedValue = createMemo(
    () => translations().values[currentLocale()] ?? "",
  )

  const translationsDirty = createMemo(
    () => JSON.stringify(translations()) !== baseline(),
  )

  async function scopedOnChange(text: string) {
    const values = mergeTranslationValues(translations().values, {
      [currentLocale()]: text,
    })
    const updated = createTranslatableData(props.defaultLocale, values)
    await commitTranslations(updated)
  }

  function syncContent(text: string, locale: Locale) {
    const values = mergeTranslationValues(translations().values, {
      [locale]: text,
    })
    setTranslations(createTranslatableData(props.defaultLocale, values))
  }

  function resetLocale() {
    setCurrentLocale(props.defaultLocale)
  }

  function getValueForLocale() {
    return (
      translations().values[props.defaultLocale] ??
      Object.values(translations().values)[0] ??
      ""
    )
  }

  async function setTranslationsFromMap(
    translationMap: Partial<Record<Locale, string>>,
    options?: {
      persist?: boolean
    },
  ) {
    const values = mergeTranslationValues(translations().values, translationMap)
    const updated = createTranslatableData(props.defaultLocale, values)
    if (
      options?.persist === false &&
      props.propagateGeneratedTranslations === false
    ) {
      setTranslations(updated)
      return
    }
    await commitTranslations(updated, options)
  }

  async function commitTranslations(
    updated: TranslatableData,
    options?: {
      persist?: boolean
    },
  ) {
    setTranslations(updated)
    await props.onChange(serializeTranslatableData(updated))
    if (options?.persist === false) return
    setBaseline(JSON.stringify(updated))
  }

  createEffect(
    on(
      () => props.value,
      newValue => {
        const currentSerialized = serializeTranslatableData(translations())
        if (newValue === currentSerialized) return
        const parsed = parseTranslatableValue(newValue, props.defaultLocale)
        setTranslations(parsed)
        setBaseline(JSON.stringify(parsed))
      },
    ),
  )

  onMount(() => {
    if (groupContext && props.name && props.format) {
      groupContext.registerField({
        name: props.name,
        format: props.format,
        getValue: getValueForLocale,
        setTranslations: setTranslationsFromMap,
      })
    }
  })

  onCleanup(() => {
    if (groupContext && props.name) {
      groupContext.unregisterField(props.name)
    }
  })

  const contextValue: TranslatableFieldContext = {
    currentLocale,
    setCurrentLocale,
    get availableLocales() {
      return props.availableLocales
    },
    get defaultLocale() {
      return props.defaultLocale
    },
    isDirty: translationsDirty,
    syncContent,
    resetLocale,
  }

  return (
    <TranslatableFieldContext.Provider value={contextValue}>
      <div class={wrapperClass}>
        {props.children(scopedValue, scopedOnChange)}
      </div>
    </TranslatableFieldContext.Provider>
  )
}

export function LanguageSelector() {
  const fieldContext = useTranslatableField()
  const groupContext = useTranslatableFieldGroup()
  const context = fieldContext ?? groupContext
  if (!context) return null

  const { LL } = useI18nContext()

  const localeItems = createMemo(() =>
    [
      context.defaultLocale,
      ...context.availableLocales.filter(l => l !== context.defaultLocale),
    ].map(locale => ({
      value: locale,
      label:
        locale === context.defaultLocale
          ? `${getLanguageLabel(locale)} (${LL().common.defaultLanguage()})`
          : getLanguageLabel(locale),
    })),
  )

  return (
    <Show when={localeItems().length > 1}>
      <div class={selectWrapperClass}>
        <Icon name="languages" size="18" color="sand500" />
        <Mini.Select
          items={localeItems()}
          value={context.currentLocale()}
          onChange={locale => {
            if (locale && isLocale(locale)) context.setCurrentLocale(locale)
          }}
        />
        <Show when={groupContext}>
          <GenerateTranslationsButton />
        </Show>
      </div>
    </Show>
  )
}

const TranslateFieldsMutation = graphql(
  `
  mutation TranslateFields($fields: [TranslateFieldInput!]!) {
    translateFields(fields: $fields) {
      ... on MutationTranslateFieldsSuccess {
        data {
          results
        }
      }
      ...ErrorFragment
    }
  }
  `,
  [ErrorFragment],
)

function isJSONContent(value: unknown): value is JSONContent {
  return typeof value === "object" && value !== null
}

function isTranslateResultItem(
  val: unknown,
): val is { id: string; value: string } {
  if (typeof val !== "object" || val === null) return false
  const obj = val as Record<string, unknown>
  return typeof obj.id === "string" && typeof obj.value === "string"
}

function hasTranslateResultsData(data: unknown): data is {
  results: string
} {
  if (typeof data !== "object" || data === null) return false
  return typeof (data as { results?: unknown }).results === "string"
}

function GenerateTranslationsButton() {
  const groupContext = useTranslatableFieldGroup()
  const { LL } = useI18nContext()
  const [generatedCount, setGeneratedCount] = createSignal<number | null>(null)

  const [, translateFields] = createMutationWithErrorHandling(
    TranslateFieldsMutation,
    "translateFields",
  )

  if (!groupContext) return null

  const defaultLocale = groupContext.defaultLocale
  const currentLocale = groupContext.currentLocale
  const {
    isTranslating,
    setIsTranslating,
    getRegisteredFields,
    availableLocales,
  } = groupContext
  const isDefaultLocaleSelected = createMemo(
    () => currentLocale() === defaultLocale,
  )
  createEffect(
    on(currentLocale, () => {
      setGeneratedCount(null)
    }),
  )

  async function handleGenerateTranslations() {
    const targetLocales = isDefaultLocaleSelected()
      ? availableLocales.filter(l => l !== defaultLocale)
      : [currentLocale()]

    if (targetLocales.length === 0) return

    setGeneratedCount(null)

    setIsTranslating(true)

    try {
      const registeredFields = getRegisteredFields()

      const fields = Array.from(registeredFields.values()).flatMap(reg => {
        let value = reg.getValue()
        if (reg.format === "tiptap" && value) {
          try {
            const parsed: unknown = JSON.parse(value)
            if (isJSONContent(parsed))
              value = generateHTML(parsed, extensions())
          } catch {
            // invalid JSON, keep original value
          }
        }
        return targetLocales.map(locale => ({
          id: `${reg.name}:${locale}`,
          value,
          fromLocale: defaultLocale,
          toLocale: locale,
        }))
      })

      if (fields.length === 0) return

      const result = await translateFields({ fields })

      if (!result.success || !hasTranslateResultsData(result.data)) return

      const parsedItems = parseTranslationResults(result.data.results)
      if (parsedItems.length === 0) return

      setGeneratedCount(getGeneratedLocalesCount(parsedItems))

      const groupedByField = groupParsedResultsByField(parsedItems)
      for (const [fieldKey, translationMap] of groupedByField) {
        const field = registeredFields.get(fieldKey)
        if (!field) continue

        const processed = processFieldTranslations(field.format, translationMap)
        await field.setTranslations(processed, { persist: false })
      }
    } catch (error) {
      console.error(error)
    } finally {
      setIsTranslating(false)
    }
  }

  return (
    <Show
      when={generatedCount() === null}
      fallback={
        <div class={[selectWrapperClass, generatedStatusClass].join(" ")}>
          <Icon name="check" size="18" />
          {`generated ${generatedCount()} ${
            generatedCount() === 1 ? "translation" : "translations"
          }`}
        </div>
      }
    >
      <Button
        type="button"
        hue="sand"
        kind="ghost"
        size="s"
        onClick={handleGenerateTranslations}
        disabled={isTranslating()}
      >
        {isTranslating()
          ? LL().common.translating()
          : isDefaultLocaleSelected()
            ? LL().common.translateAll()
            : LL().common.translateFrom({
                language: getLanguageLabel(defaultLocale),
              })}
      </Button>
    </Show>
  )
}

function createTranslatableData(
  defaultLocale: Locale,
  values: Partial<Record<Locale, string>>,
): TranslatableData {
  return {
    primaryLocale: defaultLocale,
    values,
  }
}

function mergeTranslationValues(
  existing: Partial<Record<Locale, string>>,
  updates: Partial<Record<Locale, string>>,
) {
  const merged = { ...existing }
  for (const [locale, value] of Object.entries(updates)) {
    if (!isLocale(locale)) continue
    if (value) {
      merged[locale] = value
      continue
    }
    delete merged[locale]
  }
  return merged
}

function serializeTranslatableData(data: TranslatableData) {
  if (Object.keys(data.values).length === 0) return ""
  return JSON.stringify(data)
}

interface ParsedTranslationResultItem {
  fieldKey: string
  locale: Locale
  value: string
}

function parseTranslationResults(
  rawResults: string,
): ParsedTranslationResultItem[] {
  if (!rawResults) return []

  const parsed = parseJSON<unknown>(rawResults)
  if (!Array.isArray(parsed)) return []

  const result: ParsedTranslationResultItem[] = []
  for (const item of parsed) {
    if (!isTranslateResultItem(item)) continue
    const parsedId = parseTranslationResultId(item.id)
    if (!parsedId) continue
    result.push({
      fieldKey: parsedId.fieldKey,
      locale: parsedId.locale,
      value: item.value,
    })
  }
  return result
}

function parseTranslationResultId(id: string): {
  fieldKey: string
  locale: Locale
} | null {
  const separatorIndex = id.indexOf(":")
  if (separatorIndex <= 0) return null

  const fieldKey = id.slice(0, separatorIndex)
  const locale = id.slice(separatorIndex + 1)
  if (!fieldKey || !isLocale(locale)) return null

  return {
    fieldKey,
    locale,
  }
}

function getGeneratedLocalesCount(items: ParsedTranslationResultItem[]) {
  return new Set(items.map(item => item.locale)).size
}

function groupParsedResultsByField(items: ParsedTranslationResultItem[]) {
  const grouped = new Map<string, Partial<Record<Locale, string>>>()
  for (const item of items) {
    const currentGroup = grouped.get(item.fieldKey) ?? {}
    currentGroup[item.locale] = item.value
    grouped.set(item.fieldKey, currentGroup)
  }
  return grouped
}

function processFieldTranslations(
  format: "text" | "tiptap",
  values: Partial<Record<Locale, string>>,
) {
  if (format === "text") return values

  const processed: Partial<Record<Locale, string>> = {}
  for (const locale of Object.keys(values) as Locale[]) {
    const text = values[locale]
    if (!text) continue
    processed[locale] = JSON.stringify(generateJSON(text, extensions()))
  }
  return processed
}

function parseJSON<T>(value: string): T | null {
  try {
    return JSON.parse(value) as T
  } catch {
    return null
  }
}
